import React, { useState, useEffect } from "react";
import SEO from "../seo";
const Payments = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "payment-providers",
        "additional-payment-methods",
        "supported-payment-methods",
        "credit-cards",
        "bnpl",
        "bank-redirects",
        "digital-wallets",
        "paypal-standalone",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Payments | xPage Help Center"
        description="Learn how to configure payments in xPage. Set up providers, enable credit cards, BNPL, bank redirects, digital wallets, PayPal, and Cash on Delivery to offer customers secure and flexible checkout options."
        keywords="xPage payments, payment providers, credit cards, PayPal, Cash on Delivery, BNPL, bank redirects, digital wallets, Apple Pay, Google Pay, checkout methods, Stripe payments"
        canonical="https://help.xpage.ai/help/settings/Payments"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Payments
          </h1>
          <p className="text-sm text-systemLabel">
            The Payments section in Xpage empowers you to control how customers
            complete their purchases, offering a seamless, secure, and versatile
            checkout experience. By providing multiple payment options, you can
            cater to a global audience, reduce friction at checkout, and
            maximize conversions.
          </p>
        </header>

        {/* Payment Providers Section */}
        <section id="payment-providers" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Payment Providers
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Third-Party Integration
                </h3>
                <p className="text-sm text-systemLabel">
                  Seamlessly connect third-party payment providers to accept
                  online payments.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Transaction Fees
                </h3>
                <p className="text-sm text-systemLabel">
                  External providers incur a 1% transaction fee, ensuring
                  transparency for cost management.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Configuration
                </h3>
                <p className="text-sm text-systemLabel">
                  Proper configuration ensures smooth transaction processing and
                  minimizes payment failures.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Payment Methods Section */}
        <section id="additional-payment-methods" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Additional Payment Methods
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-6">
              {/* PayPal */}
              <div id="paypal" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  PayPal
                </h3>
                <p className="text-sm text-systemLabel">
                  Integrate PayPal via a standalone method, separate from
                  Stripe.
                </p>
                <p className="text-sm text-systemLabel mt-2">
                  Ideal for customers who prefer using their PayPal account for
                  convenience and security.
                </p>
              </div>

              {/* Cash on Delivery */}
              <div id="cash-on-delivery" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Cash on Delivery (COD)
                </h3>
                <p className="text-sm text-systemLabel">
                  Activate Cash on Delivery as an alternative payment option.
                </p>
                <p className="text-sm text-systemLabel mt-2">
                  Customize COD methods and select specific landing pages where
                  COD is available.
                </p>
                <p className="text-sm text-systemLabel mt-2">
                  Assign COD to individual products or product categories to
                  meet logistical or regional needs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Supported Payment Methods Section */}
        <section id="supported-payment-methods" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Supported Xpage Payment Methods
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-6">
              {/* Credit & Debit Cards */}
              <div id="credit-cards" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  {" "}
                  Credit & Debit Cards (Global)
                </h3>
                <p className="text-sm text-systemLabel">
                  Accept all major cards: Visa, Mastercard, American Express,
                  Discover, Diners Club, China UnionPay.
                </p>
                <p className="text-sm text-systemLabel mt-2">
                  Cards are processed securely, ensuring a smooth checkout for
                  your customers worldwide.
                </p>
              </div>

              {/* BNPL */}
              <div id="bnpl" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Buy Now, Pay Later (BNPL)
                </h3>
                <p className="text-sm text-systemLabel">
                  Offer flexible payment options through Klarna, Afterpay /
                  Clearpay, Affirm, Zip.
                </p>
                <p className="text-sm text-systemLabel mt-2">
                  BNPL options increase purchasing power, boost conversion, and
                  encourage higher order values.
                </p>
              </div>

              {/* Bank Redirects */}
              <div id="bank-redirects" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  {" "}
                  Bank Redirects / Real-Time Payments
                </h3>
                <p className="text-sm text-systemLabel mb-2">
                  Support real-time bank payments for regional markets:
                </p>
                <ul className="text-sm text-systemLabel list-disc list-inside space-y-1">
                  <li>iDEAL (Netherlands)</li>
                  <li>Bancontact (Belgium)</li>
                  <li>Przelewy24 (Poland)</li>
                  <li>Sofort (Germany, Austria)</li>
                  <li>Giropay (Germany)</li>
                  <li>TWINT (Switzerland)</li>
                  <li>FPX (Malaysia) (optional regionally)</li>
                </ul>
              </div>

              {/* Digital Wallets */}
              <div id="digital-wallets" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  {" "}
                  Digital Wallets
                </h3>
                <p className="text-sm text-systemLabel mb-2">
                  Accept popular digital wallets for frictionless one-click
                  checkout:
                </p>
                <ul className="text-sm text-systemLabel list-disc list-inside space-y-1">
                  <li>Apple Pay</li>
                  <li>Google Pay</li>
                  <li>Link by Stripe</li>
                  <li>Alipay (China/global)</li>
                  <li>Amazon Pay</li>
                  <li>WeChat Pay (China)</li>
                </ul>
                <p className="text-sm text-systemLabel mt-2">
                  Digital wallets reduce checkout friction, increase mobile
                  conversion, and provide a fast, modern payment experience.
                </p>
              </div>

              {/* PayPal Standalone */}
              <div id="paypal-standalone" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  PayPal (Standalone Integration)
                </h3>
                <p className="text-sm text-systemLabel">
                  Offer PayPal as a dedicated payment option outside of Stripe.
                </p>
                <p className="text-sm text-systemLabel mt-2">
                  Ideal for global audiences and users who prefer PayPal's
                  secure, trusted platform.
                </p>
              </div>
            </div>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                By strategically configuring Payments in Xpage, you provide
                customers with flexibility, reliability, and convenience at
                checkout. Offering multiple secure payment options not only
                improves the shopping experience but also builds trust,
                encourages repeat purchases, and drives revenue growth.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#payment-providers"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "payment-providers"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Payment Providers
              </a>
            </li>
            <li>
              <a
                href="#additional-payment-methods"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "additional-payment-methods"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Additional Payment Methods
              </a>
            </li>
            <li>
              <a
                href="#paypal"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "paypal"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                PayPal
              </a>
            </li>
            <li>
              <a
                href="#cash-on-delivery"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "cash-on-delivery"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Cash on Delivery
              </a>
            </li>
            <li>
              <a
                href="#supported-payment-methods"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "supported-payment-methods"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Supported Payment Methods
              </a>
            </li>
            <li>
              <a
                href="#credit-cards"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "credit-cards"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Credit & Debit Cards
              </a>
            </li>
            <li>
              <a
                href="#bnpl"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "bnpl"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Buy Now, Pay Later
              </a>
            </li>
            <li>
              <a
                href="#bank-redirects"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "bank-redirects"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Bank Redirects
              </a>
            </li>
            <li>
              <a
                href="#digital-wallets"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "digital-wallets"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Digital Wallets
              </a>
            </li>
            <li>
              <a
                href="#paypal-standalone"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "paypal-standalone"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                PayPal Standalone
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Payments;
